import React, { createContext, useContext, useState } from 'react';
import { MenuItem, Select } from '@mui/material';

import { DS } from './Dashboard';

/**
 * One "Last N rounds" window shared by every recent-rounds stat, so the
 * dashboard never mixes Last 10 / Last 20 / Last 30. Changing it in any
 * widget updates them all, and the choice is remembered per browser.
 */
export const ROUND_WINDOW_OPTIONS = [10, 20, 30, 50];
const DEFAULT_ROUND_WINDOW = 10;
const STORAGE_KEY = 'roundWindow';

const readStored = () => {
    try {
        const stored = Number(localStorage.getItem(STORAGE_KEY));
        return ROUND_WINDOW_OPTIONS.includes(stored) ? stored : DEFAULT_ROUND_WINDOW;
    } catch {
        return DEFAULT_ROUND_WINDOW;
    }
};

type RoundWindowContextType = { roundWindow: number; setRoundWindow: (n: number) => void };

const RoundWindowContext = createContext<RoundWindowContextType>({
    roundWindow: DEFAULT_ROUND_WINDOW,
    setRoundWindow: () => undefined,
});

export const RoundWindowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [roundWindow, setState] = useState(readStored);
    const setRoundWindow = (n: number) => {
        setState(n);
        try { localStorage.setItem(STORAGE_KEY, String(n)); } catch { /* storage unavailable */ }
    };
    return (
        <RoundWindowContext.Provider value={{ roundWindow, setRoundWindow }}>
            {children}
        </RoundWindowContext.Provider>
    );
};

export const useRoundWindow = () => useContext(RoundWindowContext);

export const RoundWindowSelect: React.FC = () => {
    const { roundWindow, setRoundWindow } = useRoundWindow();
    return (
        <Select
            size="small"
            value={roundWindow}
            onChange={e => setRoundWindow(Number(e.target.value))}
            inputProps={{ 'aria-label': 'Number of recent rounds' }}
            sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: DS.text,
                borderRadius: '8px',
                '& .MuiSelect-select': { py: 0.5, pl: 1.25 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: DS.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: DS.borderStrong },
            }}
        >
            {ROUND_WINDOW_OPTIONS.map(n => (
                <MenuItem key={n} value={n} sx={{ fontSize: '13px' }}>Last {n}</MenuItem>
            ))}
        </Select>
    );
};
